
import { Button } from "@/components/ui/button";
import { ProfilePicture } from "./ProfilePicture";
import { ProfileForm } from "./ProfileForm";
import { PasswordSection } from "./PasswordSection";
import { useLanguage } from "@/contexts/LanguageContext";
import type { ProfileData } from "@/hooks/useProfileManagement";

interface ProfileFormContainerProps {
  profile: ProfileData;
  saving: boolean;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  onProfileChange: (profile: ProfileData) => void;
  onCurrentPasswordChange: (value: string) => void;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onUpdate: () => Promise<void>;
}

export const ProfileFormContainer = ({
  profile,
  saving,
  currentPassword,
  newPassword,
  confirmPassword,
  onProfileChange,
  onCurrentPasswordChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  onUpdate,
}: ProfileFormContainerProps) => {
  const { t } = useLanguage();
  
  return (
    <form onSubmit={onSubmit}>
      {/* Profile Picture and Name Section */}
      <div className="flex p-4">
        <div className="flex w-full flex-col gap-4 items-center">
          <div className="flex gap-4 flex-col items-center">
            <ProfilePicture
              profileId={profile.id}
              pictureUrl={profile.profile_picture_url}
              onUpdate={onUpdate}
            />
            <div className="flex flex-col items-center justify-center">
              <p className="text-[#111418] text-[22px] font-bold leading-tight tracking-[-0.015em] text-center">
                {profile.full_name}
              </p>
              <p className="text-[#637588] text-base font-normal leading-normal text-center">
                {profile.email}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information Section */}
      <h2 className="text-[#111418] text-[22px] font-bold leading-tight tracking-[-0.015em] px-4 pb-3 pt-5">
        Personal Information
      </h2>
      
      <ProfileForm
        profile={profile}
        onProfileChange={onProfileChange}
        saving={saving}
      />

      {/* Password Section */}
      <h2 className="text-[#111418] text-[22px] font-bold leading-tight tracking-[-0.015em] px-4 pb-3 pt-5">
        Change Password
      </h2>
      
      <PasswordSection
        currentPassword={currentPassword}
        newPassword={newPassword}
        confirmPassword={confirmPassword}
        onCurrentPasswordChange={onCurrentPasswordChange}
        onNewPasswordChange={onNewPasswordChange}
        onConfirmPasswordChange={onConfirmPasswordChange}
      />

      {/* Save Button */}
      <div className="flex px-4 py-3 justify-end">
        <Button
          type="submit"
          disabled={saving}
          className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-[#2F80ED] hover:bg-[#1B365D] text-white text-sm font-bold leading-normal tracking-[0.015em]"
        >
          <span className="truncate">
            {saving ? t('profile.saving') : 'Save Changes'}
          </span>
        </Button>
      </div>
    </form>
  );
};
